/**
 * Phased model loader: runs load → merge (worker or main) → repair → createMesh
 * with onPhase callbacks and yieldToMain between steps so the UI stays responsive.
 * STL/PLY use worker for merge; OBJ uses existing loadOBJ with phases only for scale/clone.
 */
import * as THREE from 'three';
import * as Comlink from 'comlink';
import { loadOBJ } from './objLoader';
import { loadGeometryOnly } from './index';
import {
  mergeVerticesAndComputeNormalsOnly,
  createMesh,
  setupColorAttribute,
  setupWeightAttribute,
  validateMeshTopology,
  getRepairMaxTriangles,
  getRepairMaxBoundaryEdges,
} from './loaderUtils';
import { repairMeshTopology } from '../topologyRepair';
// import { simplifyGeometry } from './simplifyGeometry';
import { yieldToMain } from '../yieldToMain';

const DEFAULT_MERGE_TOLERANCE = 1e-4;
const DEFAULT_WELD_TOLERANCE = 1e-3;

function getTriangleCount(geometry) {
  if (geometry.index) return geometry.index.count / 3;
  return geometry.attributes.position.count / 3;
}

function getMergeTolerance() {
  const env = import.meta.env.VITE_MESH_MERGE_TOLERANCE;
  if (env !== undefined) {
    const parsed = parseFloat(env);
    if (!Number.isNaN(parsed) && parsed > 0) return parsed;
  }
  return DEFAULT_MERGE_TOLERANCE;
}

function isTopologyRepairEnabled() {
  const env = import.meta.env.VITE_MESH_REPAIR_ENABLED;
  if (env !== undefined) return env === 'true' || env === true;
  return true;
}

function isWorkerEnabled() {
  const env = import.meta.env.VITE_MODEL_LOAD_USE_WORKER;
  if (env !== undefined) return env !== 'false';
  return true;
}

function getSourceType(pathOrData) {
  if (typeof pathOrData === 'object' && pathOrData.type) return pathOrData.type;
  const path = typeof pathOrData === 'string' ? pathOrData : '';
  const ext = path.slice(path.lastIndexOf('.')).toLowerCase();
  if (ext === '.stl') return 'stl';
  if (ext === '.ply') return 'ply';
  if (ext === '.obj') return 'obj';
  return null;
}

const LOG_PREFIX = '[loadModelPhased]';

/**
 * Extract vertex color array from geometry for worker. Expands face colors to vertex colors if needed.
 */
function extractColorsForWorker(geometry) {
  if (!geometry.hasAttribute('color')) return null;
  const posAttr = geometry.attributes.position;
  const colorAttr = geometry.getAttribute('color');
  const posCount = posAttr.count;
  const colorCount = colorAttr.count;
  if (colorCount === posCount) {
    return new Float32Array(colorAttr.array);
  }
  if (colorCount === posCount / 3) {
    const vertexColors = new Float32Array(posCount * 3);
    for (let i = 0; i < colorCount; i++) {
      const r = colorAttr.getX(i);
      const g = colorAttr.getY(i);
      const b = colorAttr.getZ(i);
      for (let v = 0; v < 3; v++) {
        const j = (i * 3 + v) * 3;
        vertexColors[j] = r;
        vertexColors[j + 1] = g;
        vertexColors[j + 2] = b;
      }
    }
    return vertexColors;
  }
  return null;
}

/**
 * Build BufferGeometry from worker result (position, normal, index, optional color).
 */
function geometryFromWorkerResult(result) {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(result.position, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(result.normal, 3));
  if (result.index && result.index.length > 0) {
    geometry.setIndex(new THREE.BufferAttribute(result.index, 1));
  }
  if (result.color && result.color.length > 0) {
    geometry.setAttribute('color', new THREE.BufferAttribute(result.color, 3));
  }
  return geometry;
}

/**
 * Run merge + normals in worker; on failure run on main thread.
 */
async function mergeWithWorkerOrMain(geometry, source, tolerance, onPhase, yieldToMainFn) {
  const posAttr = geometry.attributes.position;
  const positions = new Float32Array(posAttr.array);
  const indexAttr = geometry.index;
  const index = indexAttr ? new Uint32Array(indexAttr.array) : null;
  const colors = extractColorsForWorker(geometry);

  const workerAvailable = isWorkerEnabled() && typeof Worker !== 'undefined';
  if (!workerAvailable) {
    console.log(`${LOG_PREFIX} Merge: main thread (worker disabled or unavailable)`);
    geometry = mergeVerticesAndComputeNormalsOnly(geometry, tolerance, source);
    return geometry;
  }

  let worker = null;
  try {
    console.log(`${LOG_PREFIX} Merge: worker (colors: ${colors ? 'yes' : 'no'})`);
    worker = new Worker(new URL('../../workers/modelLoad.worker.js', import.meta.url), {
      type: 'module',
    });
    const api = Comlink.wrap(worker);
    const result = await api.mergeVerticesAndNormals(positions, index, tolerance, colors);
    worker.terminate();
    worker = null;
    geometry.dispose();
    return geometryFromWorkerResult(result);
  } catch (err) {
    if (worker) {
      try {
        worker.terminate();
      } catch (_) {}
    }
    console.warn(`${LOG_PREFIX} Worker merge failed, fallback to main thread:`, err);
    geometry = mergeVerticesAndComputeNormalsOnly(geometry, tolerance, source);
    return geometry;
  }
}

/**
 * Load model with phased steps and optional worker. Calls onPhase(message) and yieldToMainFn() between steps.
 * @param {string|Object} pathOrData - Model source (path or { type, data })
 * @param {Object} options - { onPhase: (message: string) => void, yieldToMain: () => Promise<void> }
 * @returns {Promise<THREE.Mesh>}
 */
export async function loadModelPhased(pathOrData, options = {}) {
  const onPhase = options.onPhase || (() => {});
  const yieldToMainFn = options.yieldToMain || yieldToMain;
  const source = getSourceType(pathOrData) || 'unknown';

  console.log(`${LOG_PREFIX} Config: worker=${isWorkerEnabled() ? 'enabled' : 'disabled'}, topologyRepair=${isTopologyRepairEnabled() ? 'enabled' : 'disabled'}, source=${source}`);

  const isCheckpoint = pathOrData?.source === 'checkpoint';

  if (source === 'obj') {
    console.log(`${LOG_PREFIX} OBJ path: loading on main thread (full pipeline)`);
    onPhase('Loading file…');
    await yieldToMainFn();
    const objPayload = pathOrData?.data !== undefined ? pathOrData.data : pathOrData;
    // These two flags used to stop at the STL/PLY branch, so a checkpoint OBJ was
    // re-rotated on every reload and a finalized OBJ took the full weld.
    const mesh = await loadOBJ(objPayload, {
      skipOrientation: isCheckpoint,
      preprocessed: pathOrData?.preprocessed === true,
    });
    await yieldToMainFn();
    console.log(`${LOG_PREFIX} OBJ load done`);
    return mesh;
  }

  if (source !== 'stl' && source !== 'ply') {
    throw new Error(`loadModelPhased does not support type: ${source}`);
  }

  // Finalized manufacturing meshes (locking / final-export STL) are display meshes:
  // large CSG-derived triangle soup that we do not edit vertex-by-vertex here.
  // Welding them via BufferGeometryUtils.mergeVertices can hang for a long time,
  // so skip the merge + topology repair for them and just compute normals. The
  // rectification checkpoint (PLY) is NOT preprocessed — it keeps the full weld so
  // its editing tools retain indexed adjacency.
  const isPreprocessed = pathOrData?.preprocessed === true;

  console.log(`${LOG_PREFIX} Step: Loading file (main thread, checkpoint=${isCheckpoint}, preprocessed=${isPreprocessed})`);
  onPhase('Loading file…');
  let geometry = await loadGeometryOnly(pathOrData, { skipOrientation: isCheckpoint });
  await yieldToMainFn();

  console.log(`${LOG_PREFIX} Step: Centering (already done in loader)`);
  onPhase('Centering model…');
  await yieldToMainFn();

  console.log(`${LOG_PREFIX} Step: Merging vertices…`);
  onPhase('Merging vertices…');
  await yieldToMainFn();
  const tolerance = getMergeTolerance();
  if (isPreprocessed) {
    console.log(`${LOG_PREFIX} Merge: skipped (preprocessed mesh) — computing normals only`);
    geometry.computeVertexNormals();
  } else {
    geometry = await mergeWithWorkerOrMain(geometry, source.toUpperCase(), tolerance, onPhase, yieldToMainFn);
  }
  await yieldToMainFn();

  const triangleCount = getTriangleCount(geometry);
  const maxRepairTriangles = getRepairMaxTriangles();
  const isLargeMesh = triangleCount > maxRepairTriangles;
  const repairEnabled = isTopologyRepairEnabled() && !isPreprocessed && !isLargeMesh;
  if (repairEnabled) {
    console.log(`${LOG_PREFIX} Step: Repairing topology (main thread)`);
    onPhase('Loading…');
    await yieldToMainFn();
    const verbose = import.meta.env.VITE_MESH_REPAIR_VERBOSE === 'true';
    repairMeshTopology(geometry, {
      weldTolerance: DEFAULT_WELD_TOLERANCE,
      removeDuplicates: true,
      verbose,
      maxBoundaryEdges: getRepairMaxBoundaryEdges(),
    });
    await yieldToMainFn();
  } else if (isLargeMesh) {
    console.warn(
      `${LOG_PREFIX} Step: Repairing topology (skipped — ${Math.round(triangleCount).toLocaleString()} triangles > ${maxRepairTriangles.toLocaleString()} limit)`
    );
  } else {
    console.log(`${LOG_PREFIX} Step: Repairing topology (disabled)`);
  }

  // console.log(`${LOG_PREFIX} Step: Simplifying mesh (meshoptimizer)…`);
  // onPhase('Optimizing mesh…');
  // await yieldToMainFn();
  // const simplifyResult = await simplifyGeometry(geometry, { source: source.toUpperCase() });
  // geometry = simplifyResult.geometry;
  // await yieldToMainFn();

  console.log(`${LOG_PREFIX} Step: Finalizing mesh (main thread)`);
  onPhase('Finalizing mesh…');
  // validateMeshTopology walks every edge into a string-keyed Map; on a multi-
  // million-triangle scan that alone costs seconds and hundreds of MB, so skip
  // it for the same meshes we skip repair on. It is diagnostics only.
  const topology = isLargeMesh ? null : validateMeshTopology(geometry, source.toUpperCase());
  if (source === 'stl') {
    setupColorAttribute(geometry, { expandFaceColors: true });
  } else {
    setupColorAttribute(geometry);
  }
  setupWeightAttribute(geometry);
  const meshName = source === 'stl' ? 'STLMesh' : 'PLYMesh';
  const mesh = createMesh(geometry, meshName);
  // Surface load-time stats so the caller can persist them in the store.
  mesh.userData.meshStats = { simplify: null, topology };
  await yieldToMainFn();

  console.log(`${LOG_PREFIX} Phased load done (${source})`);
  return mesh;
}
